import type { JobPage } from "./types";

export const jobPages: JobPage[] = [
  {
    slug: "software-engineer",
    title: { ar: "مهندس برمجيات", en: "Software Engineer" },
    category: { ar: "التقنية", en: "Technology" },
    metaTitle: {
      ar: "سيرة ذاتية لمهندس برمجيات: قالب ونصائح وأمثلة",
      en: "Software Engineer Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "تعلّم كيف تكتب سيرة ذاتية لمهندس برمجيات تجتاز أنظمة التوظيف، مع أمثلة جاهزة للإنجازات وكلمات مفتاحية ومهارات أساسية.",
      en: "Learn how to write a software engineer resume that beats applicant tracking systems, with ready achievement examples, keywords, and core skills.",
    },
    overview: [
      {
        ar: "وظيفة مهندس البرمجيات من أكثر الوظائف طلبًا، والمنافسة عليها قوية. سيرتك يجب أن تُظهر أثرك التقني بأرقام واضحة، لا مجرد قائمة بالتقنيات التي استخدمتها.",
        en: "Software engineering is one of the most in-demand roles, and competition is strong. Your resume must show your technical impact with clear numbers, not just a list of technologies you used.",
      },
      {
        ar: "ركّز على ما بنيته، والمشكلة التي حللتها، والنتيجة التي حققها عملك للمستخدم أو للشركة. هذا ما يميّز سيرتك عن مئات الطلبات المتشابهة.",
        en: "Focus on what you built, the problem you solved, and the result your work delivered for users or the business. That is what sets your resume apart from hundreds of similar applications.",
      },
    ],
    coreSkills: [
      { ar: "لغات البرمجة (مثل JavaScript وPython)", en: "Programming languages (e.g. JavaScript, Python)" },
      { ar: "هياكل البيانات والخوارزميات", en: "Data structures and algorithms" },
      { ar: "تطوير الواجهات والخدمات الخلفية", en: "Front-end and back-end development" },
      { ar: "قواعد البيانات وتصميمها", en: "Databases and schema design" },
      { ar: "أنظمة التحكم بالإصدارات (Git)", en: "Version control systems (Git)" },
      { ar: "الاختبار وضمان الجودة", en: "Testing and quality assurance" },
      { ar: "العمل ضمن منهجيات رشيقة", en: "Working within agile methodologies" },
    ],
    sampleBullets: [
      { ar: "طوّرت واجهة برمجية خفضت زمن الاستجابة بنسبة ٤٠٪ لأكثر من ١٠ آلاف مستخدم يوميًا.", en: "Built an API that cut response time by 40% for more than 10,000 daily users." },
      { ar: "قدت ترحيل نظام قديم إلى بنية حديثة دون انقطاع للخدمة.", en: "Led the migration of a legacy system to a modern architecture with zero downtime." },
      { ar: "أضفت تغطية اختبارات رفعت موثوقية الإصدارات وقلّلت الأعطال بنسبة ٣٠٪.", en: "Added test coverage that improved release reliability and reduced incidents by 30%." },
      { ar: "تعاونت مع فريق المنتج لإطلاق ميزة استخدمها ٢٥٪ من قاعدة العملاء خلال شهر.", en: "Partnered with the product team to ship a feature adopted by 25% of the customer base within a month." },
    ],
    atsKeywords: [
      { ar: "تطوير برمجيات", en: "Software development" },
      { ar: "واجهات برمجية REST", en: "REST APIs" },
      { ar: "اختبار الوحدات", en: "Unit testing" },
      { ar: "CI/CD", en: "CI/CD" },
      { ar: "حلّ المشكلات", en: "Problem solving" },
      { ar: "قواعد البيانات", en: "Databases" },
      { ar: "العمل الجماعي", en: "Collaboration" },
    ],
    sections: [
      {
        heading: { ar: "كيف ترتّب خبراتك التقنية", en: "How to Structure Your Technical Experience" },
        body: [
          {
            ar: "ابدأ كل خبرة بفعل قوي ثم اذكر التقنية والنتيجة. تجنّب وصف المهام اليومية دون أثر، فالشركات تبحث عن مهندس يحل مشكلات لا مجرد منفّذ.",
            en: "Start each role with a strong verb, then name the technology and the result. Avoid describing daily tasks with no impact, because companies look for an engineer who solves problems, not just an executor.",
          },
        ],
      },
      {
        heading: { ar: "قسم المشاريع", en: "The Projects Section" },
        body: [
          {
            ar: "إذا كنت حديث التخرج، فقسم المشاريع لا يقل أهمية عن الخبرة. اذكر مشروعين أو ثلاثة مع رابط للكود ووصف موجز للدور والنتيجة.",
            en: "If you're a recent graduate, the projects section matters as much as experience. Include two or three projects with a code link and a brief description of your role and outcome.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "سرد كل التقنيات دون ذكر مستوى الإتقان أو السياق.", en: "Listing every technology without proficiency level or context." },
      { ar: "إغفال الأرقام والنتائج القابلة للقياس.", en: "Omitting numbers and measurable results." },
      { ar: "استخدام تصميم معقد بأعمدة يصعب على أنظمة التوظيف قراءته.", en: "Using a complex multi-column design that tracking systems struggle to read." },
      { ar: "نسخ نفس السيرة لكل وظيفة دون مطابقة الوصف الوظيفي.", en: "Reusing the same resume for every job without matching the description." },
    ],
    faqs: [
      { q: { ar: "هل أضع كل لغات البرمجة التي أعرفها؟", en: "Should I list every programming language I know?" }, a: { ar: "ركّز على ما تتقنه فعلًا وما يطلبه الإعلان. القائمة الطويلة دون عمق تضعف انطباعك.", en: "Focus on what you truly master and what the posting requires. A long list without depth weakens your impression." } },
      { q: { ar: "كم صفحة يجب أن تكون سيرتي؟", en: "How many pages should my resume be?" }, a: { ar: "صفحة واحدة للمبتدئين، وصفحتان لمن لديه خبرة واسعة. الإيجاز مع الأثر أهم من الطول.", en: "One page for juniors, two for extensive experience. Brevity with impact matters more than length." } },
    ],
  },
  {
    slug: "accountant",
    title: { ar: "محاسب", en: "Accountant" },
    category: { ar: "المالية", en: "Finance" },
    metaTitle: {
      ar: "سيرة ذاتية لمحاسب: قالب احترافي ونصائح وأمثلة",
      en: "Accountant Resume: Professional Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لمحاسب تبرز دقّتك ومهاراتك المالية، مع أمثلة إنجازات وكلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write an accountant resume that highlights your accuracy and financial skills, with achievement examples and keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "مهنة المحاسبة تقوم على الدقة والمصداقية، وسيرتك يجب أن تعكس ذلك من خلال إنجازات مالية ملموسة وأرقام تثبت أثرك على الأرباح أو الكفاءة.",
        en: "Accounting is built on accuracy and trust, and your resume should reflect that through concrete financial achievements and numbers that prove your impact on profit or efficiency.",
      },
      {
        ar: "أبرز معرفتك بالمعايير المحاسبية والأنظمة المالية، وكيف ساهمت في تقليل الأخطاء أو توفير الوقت أو تحسين التقارير.",
        en: "Highlight your knowledge of accounting standards and financial systems, and how you helped reduce errors, save time, or improve reporting.",
      },
    ],
    coreSkills: [
      { ar: "إعداد القوائم المالية", en: "Financial statement preparation" },
      { ar: "التسويات والمطابقات البنكية", en: "Reconciliations and bank matching" },
      { ar: "الضرائب والامتثال", en: "Tax and compliance" },
      { ar: "برامج المحاسبة (مثل SAP وQuickBooks)", en: "Accounting software (e.g. SAP, QuickBooks)" },
      { ar: "إدارة الميزانيات", en: "Budget management" },
      { ar: "تحليل التكاليف", en: "Cost analysis" },
      { ar: "إتقان جداول البيانات", en: "Spreadsheet proficiency" },
    ],
    sampleBullets: [
      { ar: "أنجزت الإقفال الشهري في وقت أقل بنسبة ٢٥٪ عبر أتمتة المطابقات.", en: "Completed monthly close 25% faster by automating reconciliations." },
      { ar: "اكتشفت تباينات وفّرت على الشركة أكثر من ١٠٠ ألف ريال سنويًا.", en: "Identified discrepancies that saved the company over 100,000 annually." },
      { ar: "أعددت تقارير ضريبية دقيقة دون أي ملاحظات من الجهات الرقابية.", en: "Prepared accurate tax reports with zero findings from regulators." },
      { ar: "دعمت إعداد ميزانية قسم بقيمة ٥ ملايين ريال وراقبت الالتزام بها.", en: "Supported a 5M departmental budget and monitored adherence to it." },
    ],
    atsKeywords: [
      { ar: "المحاسبة المالية", en: "Financial accounting" },
      { ar: "دفتر الأستاذ العام", en: "General ledger" },
      { ar: "المطابقات", en: "Reconciliation" },
      { ar: "الامتثال الضريبي", en: "Tax compliance" },
      { ar: "إعداد الميزانية", en: "Budgeting" },
      { ar: "التدقيق", en: "Auditing" },
      { ar: "Excel", en: "Excel" },
    ],
    sections: [
      {
        heading: { ar: "أبرز شهاداتك المهنية", en: "Highlight Your Certifications" },
        body: [
          {
            ar: "الشهادات مثل CPA أو CMA أو SOCPA تعطي سيرتك ثقلًا فوريًا. ضعها قرب الأعلى إذا كانت مطلوبة في الإعلان.",
            en: "Certifications like CPA, CMA, or SOCPA give your resume instant weight. Place them near the top if the posting requires them.",
          },
        ],
      },
      {
        heading: { ar: "أظهر أثرك على الأرقام", en: "Show Your Impact on the Numbers" },
        body: [
          {
            ar: "كل محاسب يعرف إعداد القيود، لكن القليل يثبت كيف وفّر المال أو الوقت. اربط كل مهمة بنتيجة مالية واضحة.",
            en: "Every accountant knows how to make journal entries, but few prove how they saved money or time. Tie each task to a clear financial result.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "وصف المهام بشكل عام دون إنجازات مرقّمة.", en: "Describing tasks generically without quantified achievements." },
      { ar: "إهمال ذكر البرامج المحاسبية المستخدمة.", en: "Failing to mention the accounting software used." },
      { ar: "أخطاء إملائية أو رقمية تضرّ بصورة الدقة.", en: "Spelling or numerical errors that damage the impression of accuracy." },
      { ar: "إخفاء الشهادات المهنية في نهاية الصفحة.", en: "Burying professional certifications at the bottom of the page." },
    ],
    faqs: [
      { q: { ar: "هل أذكر مستوى إتقاني لبرامج المحاسبة؟", en: "Should I state my proficiency in accounting software?" }, a: { ar: "نعم، فهو يساعد مسؤول التوظيف على تقدير جاهزيتك الفورية للعمل.", en: "Yes, it helps the recruiter gauge how quickly you can start contributing." } },
      { q: { ar: "ما أهم قسم في سيرة المحاسب؟", en: "What is the most important section in an accountant resume?" }, a: { ar: "قسم الإنجازات المالية المرقّمة، لأنه يثبت قيمتك العملية لا مجرد معرفتك النظرية.", en: "The quantified financial achievements, because they prove practical value, not just theory." } },
    ],
  },
  {
    slug: "project-manager",
    title: { ar: "مدير مشاريع", en: "Project Manager" },
    category: { ar: "الإدارة", en: "Management" },
    metaTitle: {
      ar: "سيرة ذاتية لمدير مشاريع: قالب ونصائح وأمثلة احترافية",
      en: "Project Manager Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لمدير مشاريع تبرز قيادتك ونتائجك، مع أمثلة إنجازات وكلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a project manager resume that highlights your leadership and results, with achievement examples and ATS keywords.",
    },
    overview: [
      {
        ar: "مدير المشاريع الناجح يُقاس بالنتائج: مشاريع سُلّمت في الوقت وضمن الميزانية وبجودة عالية. سيرتك يجب أن تحكي هذه القصة بالأرقام.",
        en: "A successful project manager is measured by results: projects delivered on time, within budget, and at high quality. Your resume must tell that story with numbers.",
      },
      {
        ar: "أظهر حجم المشاريع التي قدتها، وعدد أفراد الفريق، والميزانيات، والمنهجيات التي اتبعتها لتحقيق النجاح.",
        en: "Show the scale of projects you led, team size, budgets, and the methodologies you used to deliver success.",
      },
    ],
    coreSkills: [
      { ar: "تخطيط المشاريع والجدولة", en: "Project planning and scheduling" },
      { ar: "إدارة الميزانية والموارد", en: "Budget and resource management" },
      { ar: "إدارة المخاطر", en: "Risk management" },
      { ar: "منهجيات Agile وScrum وWaterfall", en: "Agile, Scrum, and Waterfall methodologies" },
      { ar: "إدارة أصحاب المصلحة", en: "Stakeholder management" },
      { ar: "قيادة الفرق", en: "Team leadership" },
      { ar: "أدوات إدارة المشاريع", en: "Project management tools" },
    ],
    sampleBullets: [
      { ar: "قدت مشروعًا بقيمة مليوني ريال وسلّمته قبل الموعد بأسبوعين.", en: "Led a 2M project and delivered it two weeks ahead of schedule." },
      { ar: "أدرت فريقًا متعدد الوظائف من ١٢ شخصًا عبر ثلاثة أقسام.", en: "Managed a cross-functional team of 12 across three departments." },
      { ar: "خفضت تجاوزات الميزانية بنسبة ١٨٪ عبر تحسين تتبّع النطاق.", en: "Reduced budget overruns by 18% through better scope tracking." },
      { ar: "طبّقت منهجية رشيقة رفعت معدل التسليم في الوقت من ٧٠٪ إلى ٩٥٪.", en: "Implemented an agile process that raised on-time delivery from 70% to 95%." },
    ],
    atsKeywords: [
      { ar: "إدارة المشاريع", en: "Project management" },
      { ar: "Scrum", en: "Scrum" },
      { ar: "إدارة المخاطر", en: "Risk management" },
      { ar: "أصحاب المصلحة", en: "Stakeholders" },
      { ar: "الميزانية", en: "Budget" },
      { ar: "القيادة", en: "Leadership" },
      { ar: "PMP", en: "PMP" },
    ],
    sections: [
      {
        heading: { ar: "اربط القيادة بالنتائج", en: "Connect Leadership to Results" },
        body: [
          {
            ar: "لا تكتفِ بقول «قدت فريقًا». اذكر حجم الفريق والمشروع والنتيجة النهائية حتى يفهم القارئ نطاق مسؤوليتك.",
            en: "Don't just say \"led a team.\" State the team size, project scale, and final outcome so the reader understands your scope of responsibility.",
          },
        ],
      },
      {
        heading: { ar: "أبرز شهادة PMP إن وُجدت", en: "Highlight PMP If You Have It" },
        body: [
          {
            ar: "شهادة PMP أو PRINCE2 من أقوى الإشارات لمدير المشاريع. ضعها بوضوح إذا كانت مطلوبة.",
            en: "A PMP or PRINCE2 certification is one of the strongest signals for a project manager. Display it clearly when required.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "وصف الدور كقائمة مهام دون نتائج.", en: "Describing the role as a task list without results." },
      { ar: "إغفال حجم المشاريع والميزانيات.", en: "Omitting project scale and budgets." },
      { ar: "عدم ذكر المنهجيات المستخدمة.", en: "Not mentioning the methodologies used." },
      { ar: "إهمال مهارات التواصل وإدارة أصحاب المصلحة.", en: "Neglecting communication and stakeholder management skills." },
    ],
    faqs: [
      { q: { ar: "هل شهادة PMP ضرورية؟", en: "Is a PMP certification necessary?" }, a: { ar: "ليست دائمًا، لكنها تعزز فرصك بقوة خاصة في المشاريع الكبيرة.", en: "Not always, but it significantly boosts your chances, especially for large projects." } },
      { q: { ar: "كيف أبرز القيادة بلا لقب إداري سابق؟", en: "How do I show leadership without a prior management title?" }, a: { ar: "اذكر المبادرات التي قدتها والفرق التي نسّقت معها حتى دون لقب رسمي.", en: "Mention initiatives you led and teams you coordinated, even without a formal title." } },
    ],
  },
  {
    slug: "registered-nurse",
    title: { ar: "ممرض", en: "Registered Nurse" },
    category: { ar: "الرعاية الصحية", en: "Healthcare" },
    metaTitle: {
      ar: "سيرة ذاتية لممرض: قالب ونصائح وأمثلة احترافية",
      en: "Registered Nurse Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لممرض تبرز مهاراتك السريرية وشهاداتك ورعايتك للمرضى، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a registered nurse resume that highlights your clinical skills, certifications, and patient care, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "مهنة التمريض تعتمد على المهارة السريرية والرحمة في آن واحد. سيرتك يجب أن توازن بين الكفاءة الفنية والقدرة على رعاية المرضى بأمان.",
        en: "Nursing relies on both clinical skill and compassion. Your resume must balance technical competence with the ability to care for patients safely.",
      },
      {
        ar: "أبرز التراخيص والشهادات والتخصصات السريرية، واذكر بيئات العمل التي خدمت فيها مثل الطوارئ أو العناية المركزة.",
        en: "Highlight your licenses, certifications, and clinical specialties, and mention the settings you served in, such as emergency or intensive care.",
      },
    ],
    coreSkills: [
      { ar: "الرعاية السريرية للمرضى", en: "Clinical patient care" },
      { ar: "إدارة الأدوية", en: "Medication administration" },
      { ar: "تقييم الحالات", en: "Patient assessment" },
      { ar: "الإنعاش القلبي (BLS/ACLS)", en: "Life support (BLS/ACLS)" },
      { ar: "توثيق السجلات الطبية", en: "Medical records documentation" },
      { ar: "مكافحة العدوى", en: "Infection control" },
      { ar: "التواصل مع الفريق الطبي", en: "Communication with the care team" },
    ],
    sampleBullets: [
      { ar: "قدّمت رعاية لأكثر من ٢٠ مريضًا يوميًا في قسم الطوارئ بكفاءة عالية.", en: "Provided care for more than 20 patients daily in the emergency department with high efficiency." },
      { ar: "ساهمت في خفض معدل العدوى في القسم بنسبة ١٥٪ عبر تطبيق بروتوكولات صارمة.", en: "Helped reduce the unit's infection rate by 15% by enforcing strict protocols." },
      { ar: "درّبت ٥ ممرضين جدد على إجراءات السلامة وإدارة الأدوية.", en: "Trained 5 new nurses on safety procedures and medication management." },
      { ar: "حافظت على دقّة توثيق بنسبة ١٠٠٪ في السجلات الإلكترونية.", en: "Maintained 100% documentation accuracy in electronic health records." },
    ],
    atsKeywords: [
      { ar: "رعاية المرضى", en: "Patient care" },
      { ar: "السجلات الصحية الإلكترونية", en: "Electronic health records" },
      { ar: "إدارة الأدوية", en: "Medication administration" },
      { ar: "BLS", en: "BLS" },
      { ar: "العناية المركزة", en: "Critical care" },
      { ar: "مكافحة العدوى", en: "Infection control" },
      { ar: "تقييم المرضى", en: "Patient assessment" },
    ],
    sections: [
      {
        heading: { ar: "ضع التراخيص في المقدمة", en: "Put Licenses Up Front" },
        body: [
          {
            ar: "الترخيص المهني وشهادات الإنعاش من أول ما يبحث عنه مسؤول التوظيف. اجعلها واضحة قرب الأعلى مع تواريخ الصلاحية.",
            en: "Professional licensing and life-support certifications are among the first things a recruiter checks. Make them clearly visible near the top with validity dates.",
          },
        ],
      },
      {
        heading: { ar: "حدّد تخصصك السريري", en: "Specify Your Clinical Specialty" },
        body: [
          {
            ar: "بيئات التمريض مختلفة جدًا. توضيح خبرتك في الطوارئ أو الأطفال أو الجراحة يساعد على مطابقتك للوظيفة الصحيحة.",
            en: "Nursing settings differ greatly. Clarifying your experience in emergency, pediatrics, or surgery helps match you to the right role.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "إغفال أرقام التراخيص وتواريخ صلاحية الشهادات.", en: "Omitting license numbers and certification expiry dates." },
      { ar: "وصف عام للرعاية دون ذكر التخصص أو حجم المرضى.", en: "Generic care descriptions without specialty or patient volume." },
      { ar: "إهمال المهارات السلوكية مثل التعاطف والتواصل.", en: "Neglecting soft skills like empathy and communication." },
      { ar: "تصميم مزدحم يصعب على أنظمة التوظيف قراءته.", en: "A cluttered design that tracking systems struggle to read." },
    ],
    faqs: [
      { q: { ar: "هل أذكر كل بيئات العمل التي خدمت فيها؟", en: "Should I mention every setting I worked in?" }, a: { ar: "ركّز على البيئات الأكثر صلة بالوظيفة المستهدفة لإبراز خبرتك المناسبة.", en: "Focus on the settings most relevant to the target role to highlight fitting experience." } },
      { q: { ar: "ما أهمية ذكر معدل المرضى؟", en: "Why mention patient ratios?" }, a: { ar: "يوضح قدرتك على التحمّل والكفاءة تحت الضغط، وهو مؤشر مهم لمسؤولي التوظيف.", en: "It shows your capacity and efficiency under pressure, an important signal for recruiters." } },
    ],
  },
  {
    slug: "sales-representative",
    title: { ar: "مندوب مبيعات", en: "Sales Representative" },
    category: { ar: "المبيعات", en: "Sales" },
    metaTitle: {
      ar: "سيرة ذاتية لمندوب مبيعات: قالب ونصائح وأمثلة",
      en: "Sales Representative Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لمندوب مبيعات تبرز أرقام تحقيقك للأهداف ومهاراتك في الإقناع، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a sales representative resume that highlights quota attainment and persuasion skills, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "في المبيعات، الأرقام تتحدث. سيرتك يجب أن تكون مليئة بنسب تحقيق الأهداف والإيرادات التي حقّقتها، لأن هذا ما يبحث عنه كل مدير مبيعات.",
        en: "In sales, numbers speak. Your resume should be full of quota attainment and revenue you generated, because that's what every sales manager looks for.",
      },
      {
        ar: "أبرز قدرتك على بناء العلاقات وإغلاق الصفقات والحفاظ على العملاء، مع أمثلة محددة لنجاحاتك.",
        en: "Highlight your ability to build relationships, close deals, and retain customers, with specific examples of your wins.",
      },
    ],
    coreSkills: [
      { ar: "تحقيق أهداف المبيعات", en: "Quota attainment" },
      { ar: "بناء علاقات العملاء", en: "Customer relationship building" },
      { ar: "التفاوض وإغلاق الصفقات", en: "Negotiation and closing" },
      { ar: "أنظمة إدارة العملاء (CRM)", en: "CRM systems" },
      { ar: "التنقيب عن العملاء المحتملين", en: "Lead prospecting" },
      { ar: "عرض المنتجات", en: "Product presentation" },
      { ar: "إدارة خطوط المبيعات", en: "Pipeline management" },
    ],
    sampleBullets: [
      { ar: "حققت ١٣٥٪ من هدف المبيعات السنوي لثلاث سنوات متتالية.", en: "Achieved 135% of annual sales quota for three consecutive years." },
      { ar: "نمّيت محفظة عملاء جديدة بقيمة ٨٠٠ ألف ريال خلال عام.", en: "Grew a new client portfolio worth 800,000 within one year." },
      { ar: "رفعت معدل الاحتفاظ بالعملاء من ٧٥٪ إلى ٩٠٪.", en: "Raised customer retention from 75% to 90%." },
      { ar: "أغلقت أكبر صفقة في الفريق بقيمة ٢٥٠ ألف ريال.", en: "Closed the team's largest deal valued at 250,000." },
    ],
    atsKeywords: [
      { ar: "تحقيق الأهداف", en: "Quota attainment" },
      { ar: "إدارة العملاء", en: "Account management" },
      { ar: "CRM", en: "CRM" },
      { ar: "التفاوض", en: "Negotiation" },
      { ar: "نمو الإيرادات", en: "Revenue growth" },
      { ar: "التنقيب", en: "Prospecting" },
      { ar: "إغلاق الصفقات", en: "Deal closing" },
    ],
    sections: [
      {
        heading: { ar: "اجعل الأرقام بطلة سيرتك", en: "Make Numbers the Hero" },
        body: [
          {
            ar: "كل سطر في خبرتك يجب أن يحتوي على رقم: نسبة، إيراد، عدد عملاء. السيرة الخالية من الأرقام تبدو ضعيفة في مجال المبيعات.",
            en: "Every line of your experience should contain a number: a percentage, revenue, or client count. A number-free resume looks weak in sales.",
          },
        ],
      },
      {
        heading: { ar: "أبرز معرفتك بأدوات CRM", en: "Highlight Your CRM Knowledge" },
        body: [
          {
            ar: "الإلمام بأنظمة مثل Salesforce أو HubSpot يدل على احترافيتك في إدارة خط المبيعات وتتبّع العملاء.",
            en: "Familiarity with systems like Salesforce or HubSpot signals professionalism in managing your pipeline and tracking customers.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "غياب الأرقام ونسب تحقيق الأهداف.", en: "No numbers or quota attainment figures." },
      { ar: "التركيز على المهام بدل النتائج.", en: "Focusing on tasks instead of results." },
      { ar: "عدم ذكر أدوات المبيعات المستخدمة.", en: "Not mentioning the sales tools used." },
      { ar: "إغفال مهارات بناء العلاقات.", en: "Omitting relationship-building skills." },
    ],
    faqs: [
      { q: { ar: "ماذا لو لم أحقق هدفي في عام معين؟", en: "What if I missed quota in a given year?" }, a: { ar: "ركّز على نموّك وتعلّمك ونتائجك الإيجابية الأخرى، ولا تبالغ في تضخيم الأرقام.", en: "Focus on your growth, learning, and other positive results, and don't inflate figures." } },
      { q: { ar: "هل أذكر اسم العملاء الكبار؟", en: "Should I name big clients?" }, a: { ar: "اذكر القطاع أو الحجم بدل الأسماء إذا كانت هناك سرية تعاقدية.", en: "Mention the sector or size instead of names if there's contractual confidentiality." } },
    ],
  },
  {
    slug: "graphic-designer",
    title: { ar: "مصمم جرافيك", en: "Graphic Designer" },
    category: { ar: "الإبداع والتصميم", en: "Creative & Design" },
    metaTitle: {
      ar: "سيرة ذاتية لمصمم جرافيك: قالب ونصائح وأمثلة",
      en: "Graphic Designer Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لمصمم جرافيك تبرز إبداعك وأدواتك ومعرض أعمالك، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a graphic designer resume that highlights creativity, tools, and portfolio, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "سيرة المصمم تحتاج إلى توازن دقيق: تصميم نظيف يعكس ذوقك، لكن بسيط بما يكفي ليُقرأ من أنظمة التوظيف. رابط معرض الأعمال هو نجم سيرتك.",
        en: "A designer's resume needs a careful balance: a clean design that reflects your taste, yet simple enough to be read by tracking systems. Your portfolio link is the star.",
      },
      {
        ar: "أبرز الأدوات التي تتقنها والمشاريع التي حققت أثرًا حقيقيًا للعلامات التجارية أو الحملات.",
        en: "Highlight the tools you master and the projects that delivered real impact for brands or campaigns.",
      },
    ],
    coreSkills: [
      { ar: "تصميم الهوية البصرية", en: "Visual identity design" },
      { ar: "أدوبي (Photoshop وIllustrator)", en: "Adobe (Photoshop, Illustrator)" },
      { ar: "Figma وتصميم الواجهات", en: "Figma and UI design" },
      { ar: "التايبوغرافي والتكوين", en: "Typography and composition" },
      { ar: "تصميم المطبوعات والرقمي", en: "Print and digital design" },
      { ar: "نظرية الألوان", en: "Color theory" },
      { ar: "إدارة مشاريع التصميم", en: "Design project management" },
    ],
    sampleBullets: [
      { ar: "صمّمت هوية بصرية كاملة لعلامة تجارية رفعت تفاعل الجمهور بنسبة ٤٠٪.", en: "Designed a full visual identity for a brand that raised audience engagement by 40%." },
      { ar: "أنتجت أكثر من ٢٠٠ تصميم للحملات الرقمية ضمن مواعيد ضيقة.", en: "Produced more than 200 designs for digital campaigns under tight deadlines." },
      { ar: "أعدت تصميم واجهة تطبيق حسّنت رضا المستخدمين بشكل ملحوظ.", en: "Redesigned an app interface that noticeably improved user satisfaction." },
      { ar: "قدت فريق تصميم صغيرًا في مشروع علامة تجارية وطنية.", en: "Led a small design team on a national brand project." },
    ],
    atsKeywords: [
      { ar: "تصميم جرافيك", en: "Graphic design" },
      { ar: "Adobe Creative Suite", en: "Adobe Creative Suite" },
      { ar: "الهوية البصرية", en: "Brand identity" },
      { ar: "تصميم UI/UX", en: "UI/UX design" },
      { ar: "التايبوغرافي", en: "Typography" },
      { ar: "Figma", en: "Figma" },
      { ar: "التصميم الرقمي", en: "Digital design" },
    ],
    sections: [
      {
        heading: { ar: "رابط معرض الأعمال أولًا", en: "Lead With Your Portfolio Link" },
        body: [
          {
            ar: "أهم عنصر في سيرة المصمم هو رابط واضح لمعرض أعمالك قرب الأعلى. بدونه، تفقد سيرتك نصف قيمتها.",
            en: "The most important element in a designer's resume is a clear portfolio link near the top. Without it, your resume loses half its value.",
          },
        ],
      },
      {
        heading: { ar: "وازن بين الجمال والقابلية للقراءة", en: "Balance Beauty and Readability" },
        body: [
          {
            ar: "التصميم المبالغ فيه قد يربك أنظمة التوظيف. احتفظ بنسخة بسيطة نصية للتقديم الإلكتروني ونسخة مصممة لعرضها مباشرة.",
            en: "Over-designing can confuse tracking systems. Keep a simple text version for online applications and a designed version to present directly.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "نسيان رابط معرض الأعمال.", en: "Forgetting the portfolio link." },
      { ar: "تصميم معقد يفشل في أنظمة التوظيف.", en: "A complex design that fails in tracking systems." },
      { ar: "سرد الأدوات دون مشاريع تثبت إتقانها.", en: "Listing tools without projects that prove mastery." },
      { ar: "إغفال الأثر التجاري للتصميمات.", en: "Omitting the business impact of the designs." },
    ],
    faqs: [
      { q: { ar: "هل أصمّم سيرتي بشكل مبهر؟", en: "Should I make my resume visually striking?" }, a: { ar: "نعم لكن باعتدال، واحتفظ بنسخة نصية بسيطة لأنظمة التوظيف.", en: "Yes, but moderately, and keep a simple text version for tracking systems." } },
      { q: { ar: "ماذا أضع إن لم يكن لدي خبرة كبيرة؟", en: "What if I don't have much experience?" }, a: { ar: "ركّز على المشاريع الدراسية والحرّة وأبرز معرض أعمال قويًا.", en: "Focus on academic and freelance projects and showcase a strong portfolio." } },
    ],
  },
  {
    slug: "data-analyst",
    title: { ar: "محلل بيانات", en: "Data Analyst" },
    category: { ar: "التقنية", en: "Technology" },
    metaTitle: {
      ar: "سيرة ذاتية لمحلل بيانات: قالب ونصائح وأمثلة",
      en: "Data Analyst Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لمحلل بيانات تبرز مهاراتك التحليلية وأدواتك وأثر قراراتك، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a data analyst resume that highlights analytical skills, tools, and decision impact, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "محلل البيانات يحوّل الأرقام إلى قرارات. سيرتك يجب أن تُظهر كيف ساعدت تحليلاتك في توفير المال أو تحسين الأداء أو كشف فرص جديدة.",
        en: "A data analyst turns numbers into decisions. Your resume must show how your analysis helped save money, improve performance, or uncover new opportunities.",
      },
      {
        ar: "أبرز إتقانك للأدوات التحليلية مثل SQL وExcel وأدوات التصور، مع نتائج ملموسة لكل مشروع.",
        en: "Highlight your command of analytical tools like SQL, Excel, and visualization tools, with tangible results for each project.",
      },
    ],
    coreSkills: [
      { ar: "SQL واستعلام قواعد البيانات", en: "SQL and database querying" },
      { ar: "Excel المتقدم", en: "Advanced Excel" },
      { ar: "أدوات التصور (Power BI وTableau)", en: "Visualization tools (Power BI, Tableau)" },
      { ar: "التحليل الإحصائي", en: "Statistical analysis" },
      { ar: "تنظيف البيانات", en: "Data cleaning" },
      { ar: "Python أو R", en: "Python or R" },
      { ar: "بناء لوحات المعلومات", en: "Dashboard building" },
    ],
    sampleBullets: [
      { ar: "بنيت لوحة معلومات وفّرت على الإدارة ٨ ساعات أسبوعيًا من التقارير اليدوية.", en: "Built a dashboard that saved management 8 hours of manual reporting weekly." },
      { ar: "حلّلت سلوك العملاء وكشفت فرصة رفعت الإيرادات بنسبة ١٢٪.", en: "Analyzed customer behavior and uncovered an opportunity that raised revenue by 12%." },
      { ar: "نظّفت ووحّدت مجموعة بيانات من ٥٠٠ ألف سجل لتحسين دقة التقارير.", en: "Cleaned and consolidated a 500,000-record dataset to improve reporting accuracy." },
      { ar: "أتمتت تقريرًا شهريًا قلّل الأخطاء البشرية إلى الصفر تقريبًا.", en: "Automated a monthly report that reduced human error to nearly zero." },
    ],
    atsKeywords: [
      { ar: "تحليل البيانات", en: "Data analysis" },
      { ar: "SQL", en: "SQL" },
      { ar: "Power BI", en: "Power BI" },
      { ar: "Excel", en: "Excel" },
      { ar: "التصور المرئي", en: "Data visualization" },
      { ar: "Python", en: "Python" },
      { ar: "لوحات المعلومات", en: "Dashboards" },
    ],
    sections: [
      {
        heading: { ar: "اربط التحليل بقرار الأعمال", en: "Connect Analysis to a Business Decision" },
        body: [
          {
            ar: "لا تقل «حلّلت بيانات» فقط. اشرح القرار الذي بُني على تحليلك والنتيجة التي ترتّبت عليه.",
            en: "Don't just say \"analyzed data.\" Explain the decision your analysis informed and the result that followed.",
          },
        ],
      },
      {
        heading: { ar: "أظهر مزيجًا من الأدوات", en: "Show a Mix of Tools" },
        body: [
          {
            ar: "الجمع بين SQL والتصور والإحصاء يجعلك مرشحًا متكاملًا. اذكر مستوى إتقانك لكل أداة بصدق.",
            en: "Combining SQL, visualization, and statistics makes you a well-rounded candidate. State your honest proficiency for each tool.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "وصف التحليل دون ربطه بنتيجة أعمال.", en: "Describing analysis without tying it to a business result." },
      { ar: "إغفال الأدوات والتقنيات المستخدمة.", en: "Omitting the tools and technologies used." },
      { ar: "المبالغة في المصطلحات دون أمثلة ملموسة.", en: "Overusing jargon without concrete examples." },
      { ar: "إهمال مهارة عرض النتائج لغير المختصين.", en: "Neglecting the skill of presenting results to non-experts." },
    ],
    faqs: [
      { q: { ar: "هل أحتاج Python للحصول على الوظيفة؟", en: "Do I need Python to get the job?" }, a: { ar: "ليس دائمًا؛ SQL وExcel وأداة تصور كافية لكثير من الوظائف، وPython ميزة إضافية.", en: "Not always; SQL, Excel, and a visualization tool suffice for many roles, with Python a plus." } },
      { q: { ar: "كيف أبرز مشاريعي إن كنت مبتدئًا؟", en: "How do I showcase projects as a beginner?" }, a: { ar: "اعرض مشاريع شخصية أو تدريبية مع رابط للوحات المعلومات أو الكود.", en: "Show personal or training projects with a link to dashboards or code." } },
    ],
  },
  {
    slug: "teacher",
    title: { ar: "معلم", en: "Teacher" },
    category: { ar: "التعليم", en: "Education" },
    metaTitle: {
      ar: "سيرة ذاتية لمعلم: قالب ونصائح وأمثلة احترافية",
      en: "Teacher Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لمعلم تبرز أثرك على الطلاب ومناهجك وشهاداتك، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a teacher resume that highlights student impact, curriculum, and certifications, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "سيرة المعلم تدور حول الأثر التعليمي: كيف رفعت تحصيل الطلاب وأدرت الصف وطوّرت المناهج. الأرقام والنتائج تصنع الفرق.",
        en: "A teacher's resume revolves around educational impact: how you raised student achievement, managed the classroom, and developed curriculum. Numbers and results make the difference.",
      },
      {
        ar: "أبرز تخصصك والمراحل التي درّستها وأي أساليب تدريس حديثة أو تقنيات تعليمية أتقنتها.",
        en: "Highlight your specialty, the grade levels you taught, and any modern teaching methods or educational technologies you mastered.",
      },
    ],
    coreSkills: [
      { ar: "تخطيط الدروس والمناهج", en: "Lesson and curriculum planning" },
      { ar: "إدارة الصف", en: "Classroom management" },
      { ar: "التقييم وقياس التحصيل", en: "Assessment and achievement measurement" },
      { ar: "التعلّم القائم على التقنية", en: "Technology-based learning" },
      { ar: "التواصل مع أولياء الأمور", en: "Parent communication" },
      { ar: "التعليم المتمايز", en: "Differentiated instruction" },
      { ar: "دعم ذوي الاحتياجات", en: "Supporting diverse learners" },
    ],
    sampleBullets: [
      { ar: "رفعت متوسط درجات الطلاب في مادتي بنسبة ٢٠٪ خلال عام دراسي.", en: "Raised students' average scores in my subject by 20% within one school year." },
      { ar: "صمّمت منهجًا إثرائيًا اعتمدته المدرسة لثلاثة فصول.", en: "Designed an enrichment curriculum adopted by the school across three classes." },
      { ar: "أدرت صفًا من ٣٠ طالبًا بأساليب تفاعلية حسّنت المشاركة.", en: "Managed a class of 30 with interactive methods that improved participation." },
      { ar: "دمجت أدوات تعليمية رقمية رفعت تفاعل الطلاب بشكل ملحوظ.", en: "Integrated digital learning tools that noticeably boosted student engagement." },
    ],
    atsKeywords: [
      { ar: "تخطيط المناهج", en: "Curriculum planning" },
      { ar: "إدارة الصف", en: "Classroom management" },
      { ar: "التقييم", en: "Assessment" },
      { ar: "التعليم المتمايز", en: "Differentiated instruction" },
      { ar: "التعلّم الرقمي", en: "Digital learning" },
      { ar: "تحصيل الطلاب", en: "Student achievement" },
      { ar: "التطوير المهني", en: "Professional development" },
    ],
    sections: [
      {
        heading: { ar: "أظهر أثرك على الطلاب", en: "Show Your Impact on Students" },
        body: [
          {
            ar: "بدل قول «درّست الرياضيات»، اذكر كيف تحسّن أداء الطلاب تحت إشرافك. الأثر القابل للقياس يميّزك.",
            en: "Instead of \"taught math,\" state how students improved under your guidance. Measurable impact sets you apart.",
          },
        ],
      },
      {
        heading: { ar: "أبرز الشهادات والتدريب", en: "Highlight Certifications and Training" },
        body: [
          {
            ar: "رخصة التدريس والدورات التطويرية تعزّز سيرتك. اذكرها بوضوح خاصة إن كانت مطلوبة في الإعلان.",
            en: "A teaching license and development courses strengthen your resume. List them clearly, especially when required.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "سرد المواد دون نتائج أو أثر على الطلاب.", en: "Listing subjects without results or student impact." },
      { ar: "إغفال رخصة التدريس والشهادات.", en: "Omitting the teaching license and certifications." },
      { ar: "تجاهل المهارات التقنية الحديثة.", en: "Ignoring modern technical skills." },
      { ar: "عدم تخصيص السيرة للمرحلة أو المنهج المطلوب.", en: "Not tailoring the resume to the required grade or curriculum." },
    ],
    faqs: [
      { q: { ar: "هل أذكر كل الدورات التي حضرتها؟", en: "Should I list every course I attended?" }, a: { ar: "اختر الأكثر صلة بالوظيفة لتجنّب إطالة السيرة دون فائدة.", en: "Choose the most relevant to the role to avoid padding the resume." } },
      { q: { ar: "كيف أبرز خبرتي إن كنت معلمًا جديدًا؟", en: "How do I highlight experience as a new teacher?" }, a: { ar: "ركّز على التدريب الميداني والمناهج التي أعددتها ونتائج طلابك.", en: "Focus on practicum teaching, curriculum you built, and your students' results." } },
    ],
  },
  {
    slug: "marketing-specialist",
    title: { ar: "أخصائي تسويق", en: "Marketing Specialist" },
    category: { ar: "التسويق", en: "Marketing" },
    metaTitle: {
      ar: "سيرة ذاتية لأخصائي تسويق: قالب ونصائح وأمثلة",
      en: "Marketing Specialist Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لأخصائي تسويق تبرز حملاتك ونتائجها وأدواتك، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a marketing specialist resume that highlights campaigns, results, and tools, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "أخصائي التسويق يُقاس بنتائج حملاته: نمو الجمهور والتفاعل والتحويل. سيرتك يجب أن تربط كل حملة بمقياس واضح.",
        en: "A marketing specialist is measured by campaign results: audience growth, engagement, and conversion. Your resume must tie each campaign to a clear metric.",
      },
      {
        ar: "أبرز إتقانك لقنوات التسويق المختلفة وأدوات التحليل، وكيف ساهمت في تحقيق أهداف العلامة التجارية.",
        en: "Highlight your command of various marketing channels and analytics tools, and how you helped achieve brand goals.",
      },
    ],
    coreSkills: [
      { ar: "تسويق المحتوى", en: "Content marketing" },
      { ar: "إدارة وسائل التواصل", en: "Social media management" },
      { ar: "تحسين محركات البحث", en: "Search engine optimization" },
      { ar: "التسويق عبر البريد", en: "Email marketing" },
      { ar: "تحليل الأداء", en: "Performance analytics" },
      { ar: "إدارة الحملات المدفوعة", en: "Paid campaign management" },
      { ar: "بناء العلامة التجارية", en: "Brand building" },
    ],
    sampleBullets: [
      { ar: "قدت حملة محتوى رفعت الزيارات العضوية بنسبة ٦٠٪ خلال ستة أشهر.", en: "Led a content campaign that raised organic traffic by 60% in six months." },
      { ar: "نمّيت متابعي القنوات الاجتماعية من ٥ آلاف إلى ٣٠ ألفًا.", en: "Grew social followers from 5,000 to 30,000." },
      { ar: "رفعت معدل التحويل في صفحات الهبوط بنسبة ٢٢٪.", en: "Increased landing-page conversion by 22%." },
      { ar: "أدرت ميزانية إعلانية بقيمة ٢٠٠ ألف ريال بعائد استثمار إيجابي.", en: "Managed a 200,000 ad budget with positive return on investment." },
    ],
    atsKeywords: [
      { ar: "التسويق الرقمي", en: "Digital marketing" },
      { ar: "تسويق المحتوى", en: "Content marketing" },
      { ar: "SEO", en: "SEO" },
      { ar: "وسائل التواصل", en: "Social media" },
      { ar: "تحليلات", en: "Analytics" },
      { ar: "الحملات", en: "Campaigns" },
      { ar: "معدل التحويل", en: "Conversion rate" },
    ],
    sections: [
      {
        heading: { ar: "اربط الحملة بالنتيجة", en: "Tie the Campaign to a Result" },
        body: [
          {
            ar: "اذكر هدف الحملة والقناة والنتيجة الرقمية. مسؤول التوظيف يريد أن يرى عائد جهودك، لا قائمة مهام.",
            en: "State the campaign goal, channel, and numeric result. The recruiter wants to see the return on your efforts, not a task list.",
          },
        ],
      },
      {
        heading: { ar: "أبرز أدوات التحليل", en: "Highlight Your Analytics Tools" },
        body: [
          {
            ar: "الإلمام بأدوات مثل Google Analytics وأدوات الإعلانات يدل على قدرتك على قياس الأثر واتخاذ قرارات مبنية على البيانات.",
            en: "Familiarity with tools like Google Analytics and ad platforms shows you can measure impact and make data-driven decisions.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "وصف الحملات دون أرقام نتائج.", en: "Describing campaigns without result numbers." },
      { ar: "إغفال القنوات والأدوات المستخدمة.", en: "Omitting the channels and tools used." },
      { ar: "التعميم دون أمثلة محددة.", en: "Generalizing without specific examples." },
      { ar: "نسخ السيرة دون مطابقة الإعلان.", en: "Reusing the resume without matching the posting." },
    ],
    faqs: [
      { q: { ar: "هل أركّز على قناة واحدة أم عدة قنوات؟", en: "Should I focus on one channel or several?" }, a: { ar: "أبرز ما يطلبه الإعلان، مع إظهار تنوّعك إن كان متاحًا.", en: "Highlight what the posting asks for, while showing breadth where relevant." } },
      { q: { ar: "ما أهم رقم أذكره؟", en: "What is the most important number to include?" }, a: { ar: "أي مقياس يرتبط بالإيراد أو التحويل أو النمو، فهو الأكثر إقناعًا.", en: "Any metric tied to revenue, conversion, or growth is the most convincing." } },
    ],
  },
  {
    slug: "human-resources-specialist",
    title: { ar: "أخصائي موارد بشرية", en: "Human Resources Specialist" },
    category: { ar: "الموارد البشرية", en: "Human Resources" },
    metaTitle: {
      ar: "سيرة ذاتية لأخصائي موارد بشرية: قالب ونصائح وأمثلة",
      en: "HR Specialist Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لأخصائي موارد بشرية تبرز خبرتك في التوظيف والعلاقات والأنظمة، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write an HR specialist resume that highlights recruiting, relations, and systems experience, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "أخصائي الموارد البشرية حلقة الوصل بين الموظف والمؤسسة. سيرتك يجب أن تظهر قدرتك على التوظيف والاحتفاظ بالمواهب وتطبيق الأنظمة بكفاءة.",
        en: "An HR specialist is the link between employees and the organization. Your resume must show your ability to recruit, retain talent, and apply policies efficiently.",
      },
      {
        ar: "أبرز خبرتك في دورة التوظيف الكاملة وأنظمة إدارة الموارد البشرية وحلّ النزاعات وتطوير الموظفين.",
        en: "Highlight your experience across the full recruitment cycle, HR information systems, conflict resolution, and employee development.",
      },
    ],
    coreSkills: [
      { ar: "التوظيف والاستقطاب", en: "Recruitment and talent acquisition" },
      { ar: "علاقات الموظفين", en: "Employee relations" },
      { ar: "أنظمة إدارة الموارد البشرية", en: "HR information systems" },
      { ar: "إدارة الأداء", en: "Performance management" },
      { ar: "سياسات العمل والامتثال", en: "Labor policy and compliance" },
      { ar: "التدريب والتطوير", en: "Training and development" },
      { ar: "إدارة التعويضات", en: "Compensation administration" },
    ],
    sampleBullets: [
      { ar: "خفضت زمن التوظيف بنسبة ٣٠٪ عبر تحسين عملية الفرز.", en: "Reduced time-to-hire by 30% by improving the screening process." },
      { ar: "رفعت معدل الاحتفاظ بالموظفين بنسبة ١٥٪ عبر برامج تفاعل.", en: "Raised employee retention by 15% through engagement programs." },
      { ar: "أدرت توظيف أكثر من ١٠٠ موظف خلال عام في قطاعات متعددة.", en: "Managed hiring of more than 100 employees in one year across multiple sectors." },
      { ar: "نفّذت نظامًا لإدارة الأداء حسّن وضوح الأهداف للموظفين.", en: "Implemented a performance management system that improved goal clarity." },
    ],
    atsKeywords: [
      { ar: "التوظيف", en: "Recruitment" },
      { ar: "علاقات الموظفين", en: "Employee relations" },
      { ar: "إدارة الأداء", en: "Performance management" },
      { ar: "HRIS", en: "HRIS" },
      { ar: "الامتثال", en: "Compliance" },
      { ar: "التعويضات", en: "Compensation" },
      { ar: "الاستقطاب", en: "Talent acquisition" },
    ],
    sections: [
      {
        heading: { ar: "أظهر أثرك على المؤسسة", en: "Show Your Impact on the Organization" },
        body: [
          {
            ar: "اربط عملك بنتائج مثل خفض زمن التوظيف أو رفع الاحتفاظ. هذه المقاييس تثبت قيمتك بوضوح.",
            en: "Tie your work to results like reduced time-to-hire or higher retention. These metrics clearly prove your value.",
          },
        ],
      },
      {
        heading: { ar: "أبرز معرفتك بالأنظمة", en: "Highlight Your Systems Knowledge" },
        body: [
          {
            ar: "الإلمام بأنظمة مثل SAP SuccessFactors أو Workday ميزة قوية في الموارد البشرية الحديثة.",
            en: "Familiarity with systems like SAP SuccessFactors or Workday is a strong asset in modern HR.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "وصف المهام دون نتائج قابلة للقياس.", en: "Describing tasks without measurable results." },
      { ar: "إغفال أنظمة الموارد البشرية المستخدمة.", en: "Omitting the HR systems used." },
      { ar: "تجاهل معرفة أنظمة العمل والامتثال.", en: "Ignoring labor law and compliance knowledge." },
      { ar: "عدم إبراز مهارات التواصل وحلّ النزاعات.", en: "Not highlighting communication and conflict-resolution skills." },
    ],
    faqs: [
      { q: { ar: "هل أذكر عدد الموظفين الذين توليت توظيفهم؟", en: "Should I mention how many employees I hired?" }, a: { ar: "نعم، فالأرقام تعطي مسؤول التوظيف فكرة عن حجم مسؤوليتك.", en: "Yes, numbers give the recruiter a sense of your scope of responsibility." } },
      { q: { ar: "ما أهم مهارة في سيرة الموارد البشرية؟", en: "What is the key skill in an HR resume?" }, a: { ar: "الموازنة بين مصلحة الموظف والمؤسسة، مدعومة بنتائج عملية.", en: "Balancing employee and organizational interests, backed by practical results." } },
    ],
  },
  {
    slug: "customer-service-representative",
    title: { ar: "موظف خدمة عملاء", en: "Customer Service Representative" },
    category: { ar: "خدمة العملاء", en: "Customer Service" },
    metaTitle: {
      ar: "سيرة ذاتية لخدمة العملاء: قالب ونصائح وأمثلة",
      en: "Customer Service Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لموظف خدمة عملاء تبرز مهاراتك في التواصل وحلّ المشكلات ورضا العملاء، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a customer service resume that highlights communication, problem-solving, and customer satisfaction, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "موظف خدمة العملاء هو وجه الشركة. سيرتك يجب أن تبرز مهاراتك في التواصل والصبر وحلّ المشكلات، مدعومة بمقاييس رضا العملاء.",
        en: "A customer service representative is the face of the company. Your resume must highlight communication, patience, and problem-solving, backed by satisfaction metrics.",
      },
      {
        ar: "أظهر قدرتك على التعامل مع حجم كبير من الطلبات مع الحفاظ على جودة الخدمة وسرعة الاستجابة.",
        en: "Show your ability to handle a high volume of requests while maintaining service quality and quick response times.",
      },
    ],
    coreSkills: [
      { ar: "التواصل الفعّال", en: "Effective communication" },
      { ar: "حلّ المشكلات", en: "Problem solving" },
      { ar: "إدارة الشكاوى", en: "Complaint handling" },
      { ar: "أنظمة التذاكر وCRM", en: "Ticketing and CRM systems" },
      { ar: "الصبر والتعامل مع الضغط", en: "Patience under pressure" },
      { ar: "البيع الإضافي", en: "Upselling" },
      { ar: "إتقان لغات متعددة", en: "Multilingual ability" },
    ],
    sampleBullets: [
      { ar: "حافظت على معدل رضا عملاء بنسبة ٩٥٪ على مدى عامين.", en: "Maintained a 95% customer satisfaction rating over two years." },
      { ar: "تعاملت مع أكثر من ٨٠ استفسارًا يوميًا مع الحفاظ على الجودة.", en: "Handled more than 80 inquiries daily while maintaining quality." },
      { ar: "خفضت زمن حلّ الشكاوى بنسبة ٢٥٪ عبر تحسين الإجراءات.", en: "Reduced complaint resolution time by 25% by improving procedures." },
      { ar: "ساهمت في بيع إضافي رفع إيراد القناة بنسبة ١٠٪.", en: "Contributed to upselling that raised channel revenue by 10%." },
    ],
    atsKeywords: [
      { ar: "خدمة العملاء", en: "Customer service" },
      { ar: "رضا العملاء", en: "Customer satisfaction" },
      { ar: "CRM", en: "CRM" },
      { ar: "إدارة الشكاوى", en: "Complaint resolution" },
      { ar: "التواصل", en: "Communication" },
      { ar: "الدعم الفني", en: "Technical support" },
      { ar: "البيع الإضافي", en: "Upselling" },
    ],
    sections: [
      {
        heading: { ar: "أبرز مقاييس الرضا", en: "Highlight Satisfaction Metrics" },
        body: [
          {
            ar: "أرقام مثل معدل الرضا وزمن الاستجابة وحجم الطلبات تثبت كفاءتك بشكل لا تستطيع العبارات العامة فعله.",
            en: "Numbers like satisfaction rate, response time, and request volume prove your efficiency in a way generic phrases cannot.",
          },
        ],
      },
      {
        heading: { ar: "أظهر مهارات التواصل", en: "Show Communication Skills" },
        body: [
          {
            ar: "اذكر القنوات التي خدمت عبرها (هاتف، بريد، محادثة) وأي لغات تتقنها، فهي ميزة كبيرة في خدمة العملاء.",
            en: "Mention the channels you served (phone, email, chat) and any languages you speak, a major asset in customer service.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "غياب مقاييس الرضا والأداء.", en: "No satisfaction or performance metrics." },
      { ar: "وصف عام للمهام دون نتائج.", en: "Generic task descriptions without results." },
      { ar: "إغفال الأنظمة والقنوات المستخدمة.", en: "Omitting the systems and channels used." },
      { ar: "تجاهل المهارات اللغوية.", en: "Ignoring language skills." },
    ],
    faqs: [
      { q: { ar: "ماذا أكتب إن لم تكن لدي خبرة؟", en: "What if I have no experience?" }, a: { ar: "ركّز على مهارات التواصل وأي عمل تعاملت فيه مع الناس، حتى التطوعي.", en: "Focus on communication skills and any people-facing work, even volunteer roles." } },
      { q: { ar: "هل أذكر مهارات الحاسب؟", en: "Should I mention computer skills?" }, a: { ar: "نعم، خاصة أنظمة التذاكر وCRM وسرعة الكتابة.", en: "Yes, especially ticketing systems, CRM, and typing speed." } },
    ],
  },
  {
    slug: "civil-engineer",
    title: { ar: "مهندس مدني", en: "Civil Engineer" },
    category: { ar: "الهندسة", en: "Engineering" },
    metaTitle: {
      ar: "سيرة ذاتية لمهندس مدني: قالب ونصائح وأمثلة",
      en: "Civil Engineer Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لمهندس مدني تبرز مشاريعك وخبرتك الفنية وشهاداتك، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a civil engineer resume that highlights projects, technical expertise, and certifications, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "سيرة المهندس المدني تدور حول المشاريع: حجمها وميزانيتها ودورك فيها. الشركات تريد دليلًا على قدرتك على التنفيذ ضمن المواصفات والمواعيد.",
        en: "A civil engineer's resume revolves around projects: their scale, budget, and your role. Companies want proof you can deliver within specifications and deadlines.",
      },
      {
        ar: "أبرز خبرتك الفنية في التصميم والإشراف والبرامج الهندسية، مع نتائج ملموسة لكل مشروع.",
        en: "Highlight your technical expertise in design, supervision, and engineering software, with tangible results for each project.",
      },
    ],
    coreSkills: [
      { ar: "التصميم الإنشائي", en: "Structural design" },
      { ar: "إدارة مواقع البناء", en: "Construction site management" },
      { ar: "AutoCAD وبرامج التصميم", en: "AutoCAD and design software" },
      { ar: "مراقبة الجودة", en: "Quality control" },
      { ar: "تقدير التكاليف", en: "Cost estimation" },
      { ar: "السلامة في المواقع", en: "Site safety" },
      { ar: "إدارة العقود", en: "Contract management" },
    ],
    sampleBullets: [
      { ar: "أشرفت على مشروع بنية تحتية بقيمة ١٠ ملايين ريال وسلّمته ضمن الميزانية.", en: "Supervised a 10M infrastructure project and delivered it within budget." },
      { ar: "أعددت تصاميم إنشائية لمبنى من ٨ طوابق وفق الأكواد المعتمدة.", en: "Prepared structural designs for an 8-story building per approved codes." },
      { ar: "خفضت هدر المواد بنسبة ١٢٪ عبر تحسين جداول التنفيذ.", en: "Reduced material waste by 12% through improved execution schedules." },
      { ar: "حافظت على سجل سلامة خالٍ من الحوادث الكبرى طوال المشروع.", en: "Maintained an incident-free major-safety record throughout the project." },
    ],
    atsKeywords: [
      { ar: "الهندسة المدنية", en: "Civil engineering" },
      { ar: "AutoCAD", en: "AutoCAD" },
      { ar: "إدارة المشاريع", en: "Project management" },
      { ar: "التصميم الإنشائي", en: "Structural design" },
      { ar: "مراقبة الجودة", en: "Quality control" },
      { ar: "السلامة", en: "Safety" },
      { ar: "تقدير التكاليف", en: "Cost estimation" },
    ],
    sections: [
      {
        heading: { ar: "اعرض مشاريعك بالأرقام", en: "Present Your Projects With Numbers" },
        body: [
          {
            ar: "لكل مشروع، اذكر القيمة والمدة ودورك والنتيجة. هذا يعطي مسؤول التوظيف صورة دقيقة عن حجم خبرتك.",
            en: "For each project, state the value, duration, your role, and the result. This gives the recruiter an accurate picture of your experience scale.",
          },
        ],
      },
      {
        heading: { ar: "أبرز الشهادات المهنية", en: "Highlight Professional Certifications" },
        body: [
          {
            ar: "العضوية في الهيئة الهندسية أو شهادات إدارة المشاريع والسلامة تقوّي سيرتك بشكل ملموس.",
            en: "Membership in the engineering authority or certifications in project management and safety strengthen your resume considerably.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "ذكر المشاريع دون قيمة أو دور محدد.", en: "Listing projects without value or a defined role." },
      { ar: "إغفال البرامج الهندسية المستخدمة.", en: "Omitting the engineering software used." },
      { ar: "تجاهل سجلات السلامة والجودة.", en: "Ignoring safety and quality records." },
      { ar: "عدم ذكر العضوية أو الشهادات المهنية.", en: "Not mentioning professional membership or certifications." },
    ],
    faqs: [
      { q: { ar: "هل أذكر كل المشاريع التي عملت بها؟", en: "Should I list every project I worked on?" }, a: { ar: "اختر أكبرها وأكثرها صلة بالوظيفة لتجنّب الإطالة.", en: "Choose the largest and most relevant to the role to avoid padding." } },
      { q: { ar: "ما أهمية ذكر الأكواد الهندسية؟", en: "Why mention engineering codes?" }, a: { ar: "تثبت التزامك بالمعايير المعتمدة، وهو أمر جوهري في الهندسة المدنية.", en: "It proves your compliance with approved standards, essential in civil engineering." } },
    ],
  },
  {
    slug: "administrative-assistant",
    title: { ar: "مساعد إداري", en: "Administrative Assistant" },
    category: { ar: "الإدارة المكتبية", en: "Administration" },
    metaTitle: {
      ar: "سيرة ذاتية لمساعد إداري: قالب ونصائح وأمثلة",
      en: "Administrative Assistant Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لمساعد إداري تبرز تنظيمك وكفاءتك ومهاراتك المكتبية، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write an administrative assistant resume that highlights organization, efficiency, and office skills, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "المساعد الإداري عمود فقري لأي مكتب. سيرتك يجب أن تظهر تنظيمك وموثوقيتك وقدرتك على إدارة المهام المتعددة بكفاءة.",
        en: "An administrative assistant is the backbone of any office. Your resume must show organization, reliability, and the ability to juggle tasks efficiently.",
      },
      {
        ar: "أبرز إتقانك لأدوات المكتب وإدارة الجداول والمراسلات والدعم التنفيذي.",
        en: "Highlight your command of office tools, calendar management, correspondence, and executive support.",
      },
    ],
    coreSkills: [
      { ar: "إدارة الجداول والمواعيد", en: "Calendar and scheduling management" },
      { ar: "حزمة Microsoft Office", en: "Microsoft Office suite" },
      { ar: "تنظيم الملفات والوثائق", en: "File and document organization" },
      { ar: "إدارة المراسلات", en: "Correspondence handling" },
      { ar: "الدعم التنفيذي", en: "Executive support" },
      { ar: "تنسيق الاجتماعات والسفر", en: "Meeting and travel coordination" },
      { ar: "إدارة الوقت", en: "Time management" },
    ],
    sampleBullets: [
      { ar: "نظّمت جداول ثلاثة مديرين تنفيذيين دون أي تعارض في المواعيد.", en: "Organized the calendars of three executives with zero scheduling conflicts." },
      { ar: "أتمتت نظام أرشفة قلّل وقت البحث عن الوثائق بنسبة ٤٠٪.", en: "Automated a filing system that cut document search time by 40%." },
      { ar: "نسّقت أكثر من ٥٠ اجتماعًا وفعالية بسلاسة خلال العام.", en: "Coordinated more than 50 meetings and events smoothly during the year." },
      { ar: "أدرت مراسلات المكتب وحافظت على ردّ خلال ٢٤ ساعة.", en: "Managed office correspondence with a consistent 24-hour response time." },
    ],
    atsKeywords: [
      { ar: "الدعم الإداري", en: "Administrative support" },
      { ar: "إدارة الجداول", en: "Scheduling" },
      { ar: "Microsoft Office", en: "Microsoft Office" },
      { ar: "تنظيم الملفات", en: "File organization" },
      { ar: "المراسلات", en: "Correspondence" },
      { ar: "إدارة الوقت", en: "Time management" },
      { ar: "الدعم التنفيذي", en: "Executive support" },
    ],
    sections: [
      {
        heading: { ar: "أظهر الكفاءة بالأرقام", en: "Show Efficiency With Numbers" },
        body: [
          {
            ar: "حتى المهام الإدارية يمكن قياسها: عدد الاجتماعات، نسبة توفير الوقت، حجم المراسلات. هذه التفاصيل تميّزك.",
            en: "Even administrative tasks can be measured: number of meetings, time saved, correspondence volume. These details set you apart.",
          },
        ],
      },
      {
        heading: { ar: "أبرز موثوقيتك", en: "Highlight Your Reliability" },
        body: [
          {
            ar: "المساعد الموثوق كنز لأي مدير. اذكر أمثلة على دقتك والتزامك بالمواعيد وسرّيتك في التعامل مع المعلومات.",
            en: "A reliable assistant is a treasure to any manager. Give examples of your accuracy, punctuality, and discretion with information.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "سرد المهام دون إظهار الكفاءة أو الأثر.", en: "Listing tasks without showing efficiency or impact." },
      { ar: "إغفال الأدوات المكتبية المتقنة.", en: "Omitting the office tools mastered." },
      { ar: "تجاهل مهارات التنظيم وإدارة الوقت.", en: "Ignoring organization and time-management skills." },
      { ar: "تصميم مزدحم يصعب قراءته.", en: "A cluttered design that is hard to read." },
    ],
    faqs: [
      { q: { ar: "كيف أبرز خبرتي إن كانت محدودة؟", en: "How do I highlight limited experience?" }, a: { ar: "ركّز على التنظيم والدقة وأي أدوار تطوعية أو تدريبية أظهرت موثوقيتك.", en: "Focus on organization, accuracy, and any volunteer or internship roles that showed reliability." } },
      { q: { ar: "هل أذكر سرعة الكتابة؟", en: "Should I mention typing speed?" }, a: { ar: "نعم إن كانت مرتفعة، فهي ميزة عملية للأدوار الإدارية.", en: "Yes if it's high, as it's a practical asset for administrative roles." } },
    ],
  },
  {
    slug: "business-analyst",
    title: { ar: "محلل أعمال", en: "Business Analyst" },
    category: { ar: "الأعمال", en: "Business" },
    metaTitle: {
      ar: "سيرة ذاتية لمحلل أعمال: قالب ونصائح وأمثلة",
      en: "Business Analyst Resume: Template, Tips & Examples",
    },
    metaDescription: {
      ar: "اكتب سيرة ذاتية لمحلل أعمال تبرز قدرتك على تحليل المتطلبات وتحسين العمليات، مع كلمات مفتاحية تجتاز أنظمة التوظيف.",
      en: "Write a business analyst resume that highlights requirements analysis and process improvement, with keywords that pass tracking systems.",
    },
    overview: [
      {
        ar: "محلل الأعمال يربط بين الأعمال والتقنية. سيرتك يجب أن تظهر قدرتك على فهم المتطلبات وتحسين العمليات وتحقيق قيمة قابلة للقياس.",
        en: "A business analyst bridges business and technology. Your resume must show your ability to understand requirements, improve processes, and deliver measurable value.",
      },
      {
        ar: "أبرز خبرتك في توثيق المتطلبات وتحليل العمليات والتعاون مع أصحاب المصلحة لتحقيق الأهداف.",
        en: "Highlight your experience in requirements documentation, process analysis, and collaboration with stakeholders to meet goals.",
      },
    ],
    coreSkills: [
      { ar: "تحليل المتطلبات", en: "Requirements analysis" },
      { ar: "نمذجة العمليات", en: "Process modeling" },
      { ar: "تحليل البيانات", en: "Data analysis" },
      { ar: "إدارة أصحاب المصلحة", en: "Stakeholder management" },
      { ar: "كتابة الوثائق الوظيفية", en: "Functional documentation" },
      { ar: "أدوات مثل SQL وExcel", en: "Tools like SQL and Excel" },
      { ar: "منهجيات Agile", en: "Agile methodologies" },
    ],
    sampleBullets: [
      { ar: "حلّلت عملية تشغيلية وأعدت تصميمها لتوفير ٢٠٠ ساعة عمل شهريًا.", en: "Analyzed and redesigned an operational process to save 200 work hours monthly." },
      { ar: "وثّقت متطلبات نظام جديد خدم ٥ أقسام دون إعادة عمل كبيرة.", en: "Documented requirements for a new system serving 5 departments with minimal rework." },
      { ar: "قدت ورش عمل مع أصحاب المصلحة لمواءمة الأهداف عبر الفرق.", en: "Led stakeholder workshops to align goals across teams." },
      { ar: "بنيت تقارير تحليلية دعمت قرارات رفعت الكفاءة بنسبة ١٥٪.", en: "Built analytical reports that supported decisions raising efficiency by 15%." },
    ],
    atsKeywords: [
      { ar: "تحليل الأعمال", en: "Business analysis" },
      { ar: "تحليل المتطلبات", en: "Requirements analysis" },
      { ar: "تحسين العمليات", en: "Process improvement" },
      { ar: "أصحاب المصلحة", en: "Stakeholders" },
      { ar: "SQL", en: "SQL" },
      { ar: "Agile", en: "Agile" },
      { ar: "التوثيق", en: "Documentation" },
    ],
    sections: [
      {
        heading: { ar: "اربط التحليل بقيمة الأعمال", en: "Tie Analysis to Business Value" },
        body: [
          {
            ar: "كل تحليل يجب أن ينتهي بنتيجة: توفير وقت، خفض تكلفة، تحسين عملية. هذا ما يميّز محلل الأعمال القوي.",
            en: "Every analysis should end with a result: time saved, cost cut, process improved. This sets a strong business analyst apart.",
          },
        ],
      },
      {
        heading: { ar: "أظهر مهارات التواصل", en: "Show Communication Skills" },
        body: [
          {
            ar: "محلل الأعمال يترجم احتياجات الأعمال إلى متطلبات واضحة. أبرز قدرتك على التواصل بين الفرق التقنية وغير التقنية.",
            en: "A business analyst translates business needs into clear requirements. Highlight your ability to communicate between technical and non-technical teams.",
          },
        ],
      },
    ],
    mistakes: [
      { ar: "وصف التحليل دون نتائج أعمال.", en: "Describing analysis without business results." },
      { ar: "إغفال الأدوات والمنهجيات المستخدمة.", en: "Omitting the tools and methodologies used." },
      { ar: "التركيز على التقنية دون فهم الأعمال.", en: "Focusing on technology without business understanding." },
      { ar: "تجاهل مهارات التواصل وإدارة أصحاب المصلحة.", en: "Ignoring communication and stakeholder skills." },
    ],
    faqs: [
      { q: { ar: "هل محلل الأعمال وظيفة تقنية؟", en: "Is business analyst a technical role?" }, a: { ar: "هي وظيفة هجينة تجمع بين فهم الأعمال والمهارات التحليلية والتقنية.", en: "It's a hybrid role combining business understanding with analytical and technical skills." } },
      { q: { ar: "ما أهم ما أبرزه في سيرتي؟", en: "What should I highlight most?" }, a: { ar: "النتائج القابلة للقياس من تحليلاتك وتحسيناتك للعمليات.", en: "The measurable results of your analysis and process improvements." } },
    ],
  },
];

export function getJobBySlug(slug: string): JobPage | undefined {
  return jobPages.find((j) => j.slug === slug);
}
